import { apiAuthCaller } from '@services/apiCaller';
import { ASYNC_API_MAX_POLL_INTERVAL, ASYNC_API_TIMEOUT } from '@config';
import {
  ApiErrorResponse,
  ApiResponse,
  ApiSuccessResponse,
  formatError,
  getRespData,
} from '@components/sources/sourceResponse';

type SubmitResult = {
  requestId: string;
};

enum AsyncApiState {
  // The channel's first answer on nearly every request; `Processing` appears once the job is
  // dequeued. Both mean "keep polling", which the loop below already does for anything that is
  // not `Completed`; naming it makes the type say what the wire does (config-backend #6595
  // review, correctness major 7).
  Accepted = 'Accepted',
  Processing = 'Processing',
  Completed = 'Completed',
}

type ApiResponseDataTypes<T> = {
  Accepted: {
    state: AsyncApiState.Accepted;
  };
  Processing: {
    state: AsyncApiState.Processing;
  };
  CompletedWithData: {
    state: AsyncApiState.Completed;
    result: ApiSuccessResponse<T>;
  };
  CompletedWithError: {
    state: AsyncApiState.Completed;
    result: ApiErrorResponse;
  };
};

type ResultResponse<T> = ApiResponse<
  | ApiResponseDataTypes<T>['Accepted']
  | ApiResponseDataTypes<T>['Processing']
  | ApiResponseDataTypes<T>['CompletedWithData']
  | ApiResponseDataTypes<T>['CompletedWithError']
>;

type AsyncApiBaseConfig = {
  resultUrl: string;
  resultQueryParams?: Record<string, string>;
};

type AsyncApiSubmitConfig = AsyncApiBaseConfig & {
  submitUrl: string;
  submitRequestData: object;
};

type AsyncApiRepollConfig = AsyncApiBaseConfig & {
  requestId: string;
};

type AsyncApiConfig = AsyncApiSubmitConfig | AsyncApiRepollConfig;

const isRepollConfig = (config: AsyncApiConfig): config is AsyncApiRepollConfig =>
  'requestId' in config;

const INITIAL_POLL_INTERVAL = 1000;
const EXPONENTIAL_POLL_MULTIPLIER = 2;

type PollSession = {
  timer: NodeJS.Timeout | null;
  cancelled: boolean;
  delay: number;
  timeElapsed: number;
  cancelPendingWait: (() => void) | null;
};

const createPollSession = (): PollSession => ({
  timer: null,
  cancelled: false,
  delay: INITIAL_POLL_INTERVAL,
  timeElapsed: 0,
  cancelPendingWait: null,
});

const throwTimeoutError = () => {
  throw {
    response: {
      data: {
        errorDetails: {
          code: 'timeout',
          message: 'query took too long to execute',
          reason: 'exceeded timeout',
        },
      },
    },
  };
};

const createPollForResult = <T>(session: PollSession) => {
  const pollForResult = async (
    requestId: string,
    resultUrl: string,
    resultQueryParams?: Record<string, string>,
  ): Promise<T | null> => {
    if (session.cancelled) {
      return null;
    }
    if (session.timeElapsed > ASYNC_API_TIMEOUT) {
      throwTimeoutError();
    }
    session.timeElapsed += session.delay;

    const { data } = await apiAuthCaller().get<ResultResponse<T>>(`${resultUrl}/${requestId}`, {
      params: resultQueryParams,
    });

    const resp = getRespData(data);
    if (resp.state === AsyncApiState.Completed) {
      return getRespData(resp.result);
    }

    return new Promise<T | null>((resolve, reject) => {
      session.cancelPendingWait = () => resolve(null);
      session.delay = Math.min(
        session.delay * EXPONENTIAL_POLL_MULTIPLIER,
        ASYNC_API_MAX_POLL_INTERVAL,
      );
      session.timer = setTimeout(async () => {
        session.cancelPendingWait = null;
        try {
          const result = await pollForResult(requestId, resultUrl, resultQueryParams);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, session.delay);
    });
  };

  return pollForResult;
};

const stopPollSession = (session: PollSession) => {
  session.cancelled = true;
  if (session.timer) {
    clearTimeout(session.timer);
    session.timer = null;
  }
  if (session.cancelPendingWait) {
    session.cancelPendingWait();
    session.cancelPendingWait = null;
  }
};

export const asyncApiHandler = <T>(config: AsyncApiConfig) => {
  const { resultUrl, resultQueryParams } = config;
  const session = createPollSession();
  const pollForResult = createPollForResult<T>(session);
  const stopResultPolling = () => stopPollSession(session);

  if (isRepollConfig(config)) {
    const { requestId } = config;
    return {
      requestPromise: pollForResult(requestId, resultUrl, resultQueryParams),
      stopResultPolling,
      getRequestId: () => requestId,
    };
  }

  const { submitUrl, submitRequestData } = config;
  let submittedRequestId: string | null = null;
  let canStartPolling = false;

  const fetchData = async () => {
    canStartPolling = true;
    const { data } = await apiAuthCaller().post<ApiResponse<SubmitResult>>(
      submitUrl,
      submitRequestData,
    );
    if (data.success) {
      submittedRequestId = data.data.requestId;
      if (canStartPolling) {
        // If this is false, it means polling should not start as stopResultPolling has been called.
        canStartPolling = false;
        return pollForResult(data.data.requestId, resultUrl, resultQueryParams);
      }
      return null as T;
    }
    throw formatError(data);
  };

  const requestPromise = new Promise<T | null>((resolve, reject) => {
    fetchData()
      .then((resp) => {
        resolve(resp);
      })
      .catch((error) => {
        reject(error);
      });
  });

  const getRequestId = () => submittedRequestId;

  const stopSubmitPolling = () => {
    stopResultPolling();
    if (canStartPolling) {
      // when polling has not started yet but submit request is not fulfilled.
      canStartPolling = false;
    }
  };

  return { requestPromise, stopResultPolling: stopSubmitPolling, getRequestId };
};
